import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSession } from "./SessionProvider";
import { useMfaGate } from "./mfa";
import { StateBlock } from "../components/StateBlock";

/**
 * The screen a person is allowed to be on before they have finished signing in.
 *
 * There is no such screen once a factor is involved, and that is what this guard exists for now.
 * `/verify` is where an unanswered challenge is answered, so it has to be reachable by exactly
 * the session that owes one, and a redirect that included it would loop.
 */
const CHALLENGE_PATH = "/verify";

/**
 * Where the person was going, in full.
 *
 * The pathname alone was what this used to send, and it was not enough. `/requests/REQ-142?flik=svar`
 * is one destination and `/requests/REQ-142` is a different one, and a notification that opens a
 * particular tab of a particular request loses the tab if only the path survives. The search string
 * and the fragment are part of the address a person clicked, so they travel with it.
 */
function intendedDestination(location: { pathname: string; search: string; hash: string }): string {
  return `${location.pathname}${location.search}${location.hash}`;
}

/**
 * Keeps a signed-out visitor off the authenticated routes, and a half-signed-in one off the rest of
 * the portal.
 *
 * This is routing, not authorisation. plan.md section 8 is explicit that a hidden control is never
 * access control, and every route behind this guard would return nothing at all without a valid
 * token, because RLS and the RPC grants decide. What the guard buys is that a signed-out person
 * lands on the login screen instead of on a shell full of empty states.
 *
 * **Until 2026-09-08 it asked one question, and it was the wrong number of questions.** `status ===
 * "signed-in"` is true of a session that has answered a password and nothing else. `useMfaGate` was
 * used by four admin routes and by nowhere else, so a client who had turned two-factor
 * authentication on signed in, was sent to `/verify` by the login screen, typed `/chat` in the
 * address bar, and had the whole portal. The same route worked from a recovery link, which means
 * anybody who reached the client's mailbox walked past the factor entirely, and not being enough on
 * its own is exactly what the mailbox was supposed to stop being.
 *
 * The real repair is in the database: `public.current_tenant_ids()` and
 * `public.current_write_tenant_ids()` are empty for a session that owes a factor, so the address bar
 * now leads to a portal with nothing in it. This half is why it leads somewhere that says so
 * instead. Both are needed and they are not alternatives: the guard below is a hidden control and
 * would be skipped the same way the old one was.
 *
 * **The gate is conditional, and it has to be.** plan.md section 4, "Optional TOTP MFA for client
 * users", and architecture.md section 4 make a client's MFA optional, so `aal2` is required of an
 * account that holds a verified factor and of no other. A client who never enrolled one sees no
 * change; `useMfaGate` answers `satisfied` for them without a challenge to offer.
 */
export function RequireSession({ children }: { children: ReactNode }) {
  const { status } = useSession();
  const location = useLocation();
  const gate = useMfaGate();

  if (status === "loading") {
    return <StateBlock kind="loading" title="Öppnar portalen" />;
  }

  if (status === "signed-out") {
    return <Navigate to="/login" replace state={{ from: intendedDestination(location) }} />;
  }

  // The challenge screen itself, and the only path a session that owes a factor may reach.
  if (location.pathname === CHALLENGE_PATH) {
    return <>{children}</>;
  }

  if (gate.isPending) {
    return <StateBlock kind="loading" title="Öppnar portalen" />;
  }

  /**
   * A gate that could not be evaluated is not a gate that opens.
   *
   * Rendering the portal here would show a factor holder every screen with nothing in it, because
   * the database has already emptied their tenant list, and they would have no way to learn why.
   * Rendering the challenge screen would be a lie to the client who has no factor and hit a network
   * error. So it says what happened.
   */
  if (gate.isError) {
    return (
      <StateBlock
        kind="error"
        title="Vi kunde inte läsa av ditt konto"
        body="Ingenting har ändrats. Ladda om sidan, eller logga in igen om det inte hjälper."
        onRetry={() => void gate.refetch()}
      />
    );
  }

  /**
   * `challenge-required` and not "anything but satisfied", which is the difference between this
   * guard and the four admin routes that already had one.
   *
   * The third answer is `enrollment-missing`: the allow-list requires a factor from this account
   * and the account has none. That person has to reach `/settings`, because that is where
   * enrolment lives and GoTrue permits `mfa.enroll` at `aal1` for an account holding no verified
   * factor. Redirecting them to `/verify` would send them to the one screen that tells them
   * to go somewhere they can no longer get to.
   *
   * It is also not a hole. `enrollment-missing` means no verified factor exists, so
   * `private.session_owes_a_factor()` is false and the database gate does not apply either; and
   * an allow-listed admin holds no membership, so every tenant-scoped read answers them with
   * nothing regardless, and `public.is_platform_admin()` refuses them at `aal1`.
   */
  if (gate.data?.kind === "challenge-required") {
    return <Navigate to={CHALLENGE_PATH} replace state={{ from: intendedDestination(location) }} />;
  }

  return <>{children}</>;
}
